import { NodeCard } from '@/modules/folders/components/NodeCard';
import { NodeList } from '@/modules/folders/components/NodeList';
import type { NodeAction } from '@/modules/folders/components/NodeMenu';
import { formatRelativeTime } from '@/shared/i18n';
import { useI18n } from '@/shared/i18n/useT';
import type { FoldersNode, FoldersNodeOpen } from '@/shared/folders';
import { LIBRARY_DOCUMENT_INFO, LibraryDocumentCard } from '../library/LibraryTab';
import type { LibraryOpen } from '../library/libraryRecents';
import { HomeCardGrid, HomeSection } from './HomeSection';
import type { HomeView } from './ViewSwitch';

interface Props {
  items: readonly FoldersNodeOpen[];
  /** `user_id → display name`, from the organization directory. */
  ownerNames: ReadonlyMap<string, string | null>;
  currentUserId: string | null;
  actionsFor: (node: FoldersNode) => readonly NodeAction[];
  view: HomeView;
  starredIds: ReadonlySet<string>;
  /** Omit when the viewer cannot star (read-only, signed out). */
  onToggleStar?: (node: FoldersNode) => void;
  /** Numtable library documents opened in this browser, merged in by date. */
  libraryItems?: readonly LibraryOpen[];
}

type Entry =
  | { kind: 'node'; openedAt: string; item: FoldersNodeOpen }
  | { kind: 'library'; openedAt: string; item: LibraryOpen };

/**
 * Last opened, as a list or as cards.
 *
 * The list is the more useful form here — who owns it, when you were last in
 * it — so it carries the Owner column the other sections leave out.
 */
export function RecentSection({ items, ownerNames, currentUserId, actionsFor, view, starredIds, onToggleStar, libraryItems = [] }: Props) {
  const { t, formatLocale } = useI18n();
  if (items.length === 0 && libraryItems.length === 0) return null;

  const entries: Entry[] = [
    ...items.map((item): Entry => ({ kind: 'node', openedAt: item.openedAt, item })),
    ...libraryItems.map((item): Entry => ({ kind: 'library', openedAt: item.openedAt, item })),
  ].sort((a, b) => Date.parse(b.openedAt) - Date.parse(a.openedAt));

  const ownerOf = (node: FoldersNode) =>
    node.createdBy && node.createdBy === currentUserId
      ? t('home.owner.me')
      : (node.createdBy ? ownerNames.get(node.createdBy) : null) ?? t('home.owner.unknown');
  const openedText = (openedAt: string) =>
    t('home.openedOn', { date: formatRelativeTime(openedAt, formatLocale) });

  return (
    <HomeSection title={t('home.lastOpened')}>
      {view === 'list' ? (
        <NodeList
          dateLabel={t('home.columns.opened')}
          rows={entries.map((entry) => {
            if (entry.kind === 'library') {
              const doc = LIBRARY_DOCUMENT_INFO.find((d) => d.id === entry.item.id)!;
              // Not a Folders node: a stand-in row with its own link, no menu, no star.
              return {
                node: {
                  id: `library:${doc.id}`, orgId: '', parentId: null, kind: 'document' as const, docType: null,
                  name: t(doc.titleKey), color: null, trashedAt: null, updatedAt: entry.openedAt, createdBy: null,
                },
                actions: [],
                owner: 'Numtable',
                date: formatRelativeTime(entry.openedAt, formatLocale),
                to: `/library/${doc.id}`,
                icon: doc.icon,
                typeLabel: t('library.readOnlyBadge'),
              };
            }
            const { node, openedAt } = entry.item;
            return {
              node,
              actions: actionsFor(node),
              owner: ownerOf(node),
              date: formatRelativeTime(openedAt, formatLocale),
              starred: starredIds.has(node.id),
              ...(onToggleStar ? { onToggleStar: () => onToggleStar(node) } : {}),
            };
          })}
        />
      ) : (
        <HomeCardGrid>
          {entries.map((entry) => entry.kind === 'library' ? (
            <li key={`library:${entry.item.id}`}>
              <LibraryDocumentCard id={entry.item.id} meta={openedText(entry.openedAt)} />
            </li>
          ) : (
            <li key={entry.item.node.id}>
              <NodeCard
                node={entry.item.node}
                actions={actionsFor(entry.item.node)}
                meta={openedText(entry.openedAt)}
                starred={starredIds.has(entry.item.node.id)}
                {...(onToggleStar ? { onToggleStar: () => onToggleStar(entry.item.node) } : {})}
              />
            </li>
          ))}
        </HomeCardGrid>
      )}
    </HomeSection>
  );
}
